/**
 * Entry point — wires React + Mantine + Auth + Router and mounts
 * the customer web portal at #root.
 *
 * THEME — sourced 1:1 from the design folder
 *   `/Users/gudupuramesh/Documents/design ui/anti Interior io ui web`
 * The `iio.css` file is the verbatim copy of `style.css` from that
 * folder. It defines every chrome / page / component class name and
 * every CSS variable (--brand red, --page-bg warm off-white, etc.)
 * the design relies on. Mantine ships with its own primitives — we
 * configure it to use Inter + red primary so the framework controls
 * match the iio.css token set.
 */
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

// MUST load AFTER Mantine's own styles so iio.css token + class rules
// override any framework defaults. iio.css applies the global body bg,
// font, and every page/chrome class used across the app.
import './theme/iio.css';

import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { initWebSentry } from './lib/sentry';

initWebSentry();

const theme = createTheme({
  // Inter primary, with system-font fallback identical to iio.css.
  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  fontFamilyMonospace:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',

  // Red primary — matches `--brand: #DC2626`. Mantine's `red`
  // palette uses shade 6 = #FA5252 by default; we want the exact
  // brand red, so individual buttons use the `.btn-primary` class
  // from iio.css when we need pixel-perfect parity.
  primaryColor: 'red',
  primaryShade: 7,

  // 8px corner radius — matches `--card-radius: 8px`.
  defaultRadius: 'sm',

  white: '#FFFFFF',
  black: '#0A0A0A',

  components: {
    Button:  { defaultProps: { radius: 'sm' } },
    Paper:   { defaultProps: { radius: 'sm' } },
    Card:    { defaultProps: { radius: 'sm' } },
    Modal:   { defaultProps: { radius: 'sm', centered: true } },
    Tooltip: { defaultProps: { radius: 'sm', withArrow: true } },
    Input:   { defaultProps: { radius: 'sm' } },
    TextInput: { defaultProps: { radius: 'sm' } },
    Textarea:  { defaultProps: { radius: 'sm' } },
    NumberInput: { defaultProps: { radius: 'sm' } },
    Select:    { defaultProps: { radius: 'sm' } },
    MultiSelect: { defaultProps: { radius: 'sm' } },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <MantineProvider theme={theme}>
      <Notifications position="top-right" />
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </MantineProvider>
  </StrictMode>,
);
